import React, { useId, useLayoutEffect, useMemo, useRef, useState } from "react";

export const ROUND_COUNT = 25; // rounds 0..24
export const LAST_ROUND = ROUND_COUNT - 1;

export const CONSENSUS_COLOR = '#34d399'; // emerald-400

// One hue per source so a line can be matched to its legend chip at a glance.
export const SOURCE_COLORS = {
  count1: '#38bdf8',
  count2: '#a78bfa',
  count3: '#fbbf24',
  count4: '#f472b6',
  count5: '#2dd4bf',
  betfair: '#fb923c',
  espn: '#818cf8',
  byb: '#a3e635',
  combined: '#38bdf8',
};

// Clean y-axis ceiling + tick step, matching the other SVG charts. maxStep caps
// how coarse the increments may get, even when that means more gridlines than
// the tick budget would normally allow.
const niceScale = (maxValue, maxTicks = 5, maxStep = Infinity) => {
  if (!maxValue || maxValue <= 0) return { max: 4, step: 1 };
  const candidates = [];
  for (let p = -1; p <= 3; p++) {
    const mag = Math.pow(10, p);
    [1, 2, 2.5, 5].forEach((m) => candidates.push(m * mag));
  }
  candidates.sort((a, b) => a - b);
  const allowed = candidates.filter((s) => s <= maxStep);
  for (const step of allowed) {
    const max = Math.ceil(maxValue / step - 1e-9) * step;
    if (max / step <= maxTicks) return { max, step };
  }
  const step = allowed.length ? allowed[allowed.length - 1] : candidates[candidates.length - 1];
  return { max: Math.ceil(maxValue / step - 1e-9) * step, step };
};

export const toCumulative = (rounds) => {
  let running = 0;
  return rounds.map((v) => {
    running += Number(v) || 0;
    return running;
  });
};

/**
 * Turns per-dataset round arrays into chart series: the consensus (the per-round
 * mean of those datasets, which is the figure the tables show) first, then one
 * series per source. sources: [{ key, label, rounds: number[25] }]
 */
export const buildDatasetSeries = (sources = []) => {
  if (!sources.length) return [];

  const consensusRounds = Array.from({ length: ROUND_COUNT }, (_, r) => {
    const sum = sources.reduce((acc, s) => acc + (Number(s.rounds[r]) || 0), 0);
    return sum / sources.length;
  });

  return [
    {
      key: 'consensus',
      label: 'Consensus',
      color: CONSENSUS_COLOR,
      rounds: consensusRounds,
      cumulative: toCumulative(consensusRounds),
    },
    ...sources.map((s) => ({
      key: s.key,
      label: s.label,
      color: SOURCE_COLORS[s.key] || '#94a3b8',
      rounds: s.rounds,
      cumulative: toCumulative(s.rounds),
    })),
  ];
};

/**
 * Season progression for a single subject, one line per enabled dataset plus a
 * highlighted consensus (the average those datasets produce). The spread
 * between lines shows how much the sources disagree over the season.
 */
const CumulativeProgressionChart = ({
  series,
  consensusIndex = 0,
  hoverSeries,
  onHoverSeries,
  ariaLabel = 'Cumulative votes by round, per dataset',
  height = 220,
  maxStep,
}) => {
  const wrapRef = useRef(null);
  const [measuredW, setMeasuredW] = useState(560);
  const [hoverRound, setHoverRound] = useState(null);
  const gradientId = `cpc-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setMeasuredW(Math.max(260, el.clientWidth));
    update();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el);
    } else {
      window.addEventListener('resize', update);
    }
    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', update);
    };
  }, []);

  const geom = useMemo(() => {
    const padL = 24;
    const padR = 10;
    const padT = 16;
    const padB = 20;
    const svgW = measuredW;
    const svgH = height;
    const innerW = svgW - padL - padR;
    const innerH = svgH - padT - padB;

    const peak = Math.max(0, ...series.map((s) => s.cumulative[LAST_ROUND] || 0));
    // Fill the axis with as many gridlines as fit at ~20px apart, so high-vote
    // subjects get finer increments instead of a coarse 0/10/20/30 scale.
    const tickBudget = Math.max(4, Math.floor(innerH / 20));
    const { max: yMax, step } = niceScale(peak, tickBudget, maxStep);

    const yFor = (v) => padT + innerH - (Math.max(0, v) / yMax) * innerH;
    const xFor = (i) => padL + (innerW * i) / LAST_ROUND;
    const slot = innerW / LAST_ROUND;

    const ticks = [];
    for (let t = 0; t <= Math.round(yMax / step); t++) {
      const v = t * step;
      ticks.push({ v, y: yFor(v) });
    }

    return { padL, padR, padT, padB, svgW, svgH, slot, yFor, xFor, ticks };
  }, [series, measuredW, height, maxStep]);

  const { padL, padR, padT, padB, svgW, svgH, slot, yFor, xFor, ticks } = geom;
  const baselineY = yFor(0);

  const pathFor = (cumulative) =>
    `M${cumulative.map((v, i) => `${xFor(i).toFixed(1)},${yFor(v).toFixed(1)}`).join(' L')}`;

  const consensus = series[consensusIndex];
  const areaPath = `${pathFor(consensus.cumulative)} L${xFor(LAST_ROUND).toFixed(1)},${baselineY.toFixed(1)} L${xFor(0).toFixed(1)},${baselineY.toFixed(1)} Z`;

  // The series the round readout tracks: whichever is hovered, else consensus.
  const activeIndex = hoverSeries != null ? hoverSeries : consensusIndex;
  const active = series[activeIndex];

  // Remounting replays the draw-in when the subject or enabled datasets change.
  const animKey = series.map((s) => `${s.key}:${(s.cumulative[LAST_ROUND] || 0).toFixed(2)}`).join('|');

  return (
    <div ref={wrapRef} className="relative w-full">
      <style>{`
        @keyframes cpcDraw { from { stroke-dashoffset: 1; } to { stroke-dashoffset: 0; } }
        .cpc-draw { animation: cpcDraw 0.9s cubic-bezier(0.22, 1, 0.36, 1) backwards; }
        @keyframes cpcFade { from { opacity: 0; } to { opacity: 1; } }
        .cpc-fade { animation: cpcFade 0.5s ease 0.35s backwards; }
      `}</style>
      <svg
        width={svgW}
        height={svgH}
        className="block"
        role="img"
        aria-label={ariaLabel}
        onMouseLeave={() => setHoverRound(null)}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={CONSENSUS_COLOR} stopOpacity="0.26" />
            <stop offset="100%" stopColor={CONSENSUS_COLOR} stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Gridlines + y labels */}
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={padL} y1={t.y} x2={svgW - padR} y2={t.y} stroke="#1e293b" strokeWidth="1" />
            <text x={padL - 6} y={t.y + 3} textAnchor="end" className="fill-slate-500" style={{ fontSize: 9, fontVariantNumeric: 'tabular-nums' }}>
              {Number.isInteger(t.v) ? t.v : t.v.toFixed(1)}
            </text>
          </g>
        ))}

        <g key={animKey}>
          {/* Consensus area sits behind every line */}
          <path d={areaPath} fill={`url(#${gradientId})`} className="cpc-fade" />

          {/* Per-source lines, then the consensus on top */}
          {series.map((s, i) => {
            if (i === consensusIndex) return null;
            const dim = hoverSeries != null && hoverSeries !== i;
            return (
              <path
                key={s.key}
                d={pathFor(s.cumulative)}
                fill="none"
                stroke={s.color}
                strokeWidth={hoverSeries === i ? 2.2 : 1.4}
                strokeLinejoin="round"
                strokeLinecap="round"
                pathLength="1"
                strokeDasharray="1"
                className="cpc-draw"
                style={{
                  animationDelay: `${Math.min(i * 60, 400)}ms`,
                  opacity: dim ? 0.16 : 0.8,
                  transition: 'opacity 0.15s, stroke-width 0.15s',
                }}
              />
            );
          })}
          <path
            d={pathFor(consensus.cumulative)}
            fill="none"
            stroke={CONSENSUS_COLOR}
            strokeWidth={hoverSeries === consensusIndex ? 3 : 2.4}
            strokeLinejoin="round"
            strokeLinecap="round"
            pathLength="1"
            strokeDasharray="1"
            className="cpc-draw"
            style={{
              opacity: hoverSeries != null && hoverSeries !== consensusIndex ? 0.35 : 1,
              transition: 'opacity 0.15s, stroke-width 0.15s',
            }}
          />
        </g>

        {/* Hover guide tracking the active series */}
        {hoverRound != null && (
          <g pointerEvents="none">
            <line x1={xFor(hoverRound)} y1={padT} x2={xFor(hoverRound)} y2={baselineY} stroke="#475569" strokeWidth="1" strokeDasharray="2 3" />
            <circle cx={xFor(hoverRound)} cy={yFor(active.cumulative[hoverRound])} r={3.4} fill={active.color} stroke="#0f172a" strokeWidth="1" />
            <text
              x={Math.min(Math.max(xFor(hoverRound), padL + 26), svgW - padR - 26)}
              y={Math.max(padT + 8, yFor(active.cumulative[hoverRound]) - 8)}
              textAnchor="middle"
              style={{ fontSize: 9, fontWeight: 700, fontVariantNumeric: 'tabular-nums', fill: active.color }}
            >
              {active.cumulative[hoverRound].toFixed(2)}
            </text>
          </g>
        )}

        {/* Round labels — every round, matching the breakdown chart above */}
        {Array.from({ length: ROUND_COUNT }, (_, i) => i).map((i) => (
          <text
            key={i}
            x={xFor(i)}
            y={svgH - 6}
            textAnchor="middle"
            className={hoverRound === i ? 'fill-slate-300' : 'fill-slate-500'}
            style={{ fontSize: 8, fontVariantNumeric: 'tabular-nums' }}
          >
            {i}
          </text>
        ))}

        {/* Hover hit areas, drawn last so they sit above the plot */}
        {Array.from({ length: ROUND_COUNT }, (_, i) => i).map((i) => (
          <rect
            key={i}
            x={xFor(i) - slot / 2}
            y={padT}
            width={slot}
            height={svgH - padT - padB}
            fill="transparent"
            onMouseEnter={() => setHoverRound(i)}
          >
            <title>{`Round ${i} — ${active.label}: ${active.cumulative[i].toFixed(2)} total (${(active.rounds[i] || 0).toFixed(2)} this round)`}</title>
          </rect>
        ))}

        {/* Baseline */}
        <line x1={padL} y1={baselineY} x2={svgW - padR} y2={baselineY} stroke="#334155" strokeWidth="1.25" />
      </svg>

      {/* Legend: hovering a chip isolates that line */}
      <div className="mt-3 pt-3 border-t border-slate-800/70 flex flex-wrap gap-1.5" onMouseLeave={() => onHoverSeries(null)}>
        {series.map((s, i) => {
          const isConsensus = i === consensusIndex;
          const dim = hoverSeries != null && hoverSeries !== i;
          return (
            <div
              key={s.key}
              onMouseEnter={() => onHoverSeries(i)}
              className={`flex items-center gap-1.5 px-2 py-1 rounded-md border transition-colors ${
                isConsensus ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-slate-900/50 border-slate-800'
              }`}
              style={{ opacity: dim ? 0.4 : 1, cursor: 'default' }}
            >
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
              <span className={`text-[10px] font-mono uppercase tracking-[0.1em] ${isConsensus ? 'text-emerald-300' : 'text-slate-400'}`}>
                {s.label}
              </span>
              <span className={`text-[11px] font-mono font-bold tabular-nums ${isConsensus ? 'text-emerald-300' : 'text-slate-300'}`}>
                {(s.cumulative[LAST_ROUND] || 0).toFixed(2)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CumulativeProgressionChart;
